import { useFirestoreDocumentData, useFirestoreDocumentMutation } from "@react-query-firebase/firestore"
import { type DocumentData, collection, deleteField, doc, getDoc, updateDoc } from "firebase/firestore"
import { useEffect, useState } from "react"
import { useAuthState } from "react-firebase-hooks/auth"
import { useMutation, useQuery, useQueryClient } from "react-query"
import { auth, db } from "./firebase"

export type useGetUserProps = {
  data: DocumentData | undefined
  isLoading: boolean
  error: Error | null
  refetch(): void
  isRefetching: boolean
}

const useGetUser = (): useGetUserProps => {
  const [user, isLoading, authError] = useAuthState(auth)
  const [error, setError] = useState<Error | null>(null)

  const {
    data,
    isLoading: isUserLoading,
    refetch,
    isRefetching,
    error: firestoreError,
  } = useFirestoreDocumentData(
    ["users", user?.uid],
    doc(db, "users", user?.uid || "a"),
    {},
    {
      enabled: !isLoading && !!user?.uid,
      refetchOnMount: true,
      onError: (err) => {
        console.error("Error fetching user data:", err)
        setError(err as Error)
      },
    },
  )

  useEffect(() => {
    if (authError) {
      console.error("Auth error in useGetUser:", authError)
      setError(authError)
    }
  }, [authError])

  useEffect(() => {
    if (firestoreError) {
      console.error("Firestore error in useGetUser:", firestoreError)
      setError(firestoreError as Error)
    }
  }, [firestoreError])

  return {
    data,
    isLoading: isUserLoading || isLoading,
    error,
    refetch,
    isRefetching,
  }
}

interface UserBookingsResult {
  data: DocumentData | null | undefined
  isLoading: boolean
  error: Error | null
  refetch(): void
}

const useGetUserBookings = (): UserBookingsResult => {
  const { data: dataUser, isLoading: isLoadingUser, error: userError } = useGetUser()
  const [userAuth, isLoadingAuth, authError] = useAuthState(auth)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    if (userError) {
      setError(userError)
    } else if (authError) {
      setError(authError)
    }
  }, [userError, authError])

  const {
    data,
    isLoading,
    refetch,
    error: bookingsError,
  } = useQuery(
    ["userscalendly", userAuth?.uid],
    async () => {
      try {
        // If user doesn't have a calendly_event_uid, return null early
        if (!dataUser?.calendly_event_uid) {
          return null
        }

        const docref = doc(collection(db, "calls"), dataUser.calendly_event_uid)
        const docSnap = await getDoc(docref)

        if (!docSnap.exists()) {
          // If the document doesn't exist but user still has calendly_event_uid,
          // it might be a sync issue. Log it for debugging.
          console.log("Call document not found but user has calendly_event_uid:", dataUser.calendly_event_uid)
          return null
        }

        return docSnap.data()
      } catch (err) {
        console.error("Error fetching user bookings:", err)
        throw err
      }
    },
    {
      refetchOnMount: true,
      refetchOnWindowFocus: true, // Refetch when user returns to the tab
      refetchInterval: 30000, // Refetch every 30 seconds to keep data in sync
      enabled: !isLoadingUser && !isLoadingAuth && !!userAuth?.uid && !!dataUser,
      onError: (err) => {
        console.error("Error in useGetUserBookings query:", err)
        setError(err as Error)
      },
      retry: 2,
    },
  )

  useEffect(() => {
    if (bookingsError) {
      setError(bookingsError as Error)
    }
  }, [bookingsError])

  return {
    data,
    isLoading: isLoading || isLoadingUser || isLoadingAuth,
    error,
    refetch,
  }
}

interface CurrentUserMutationResult {
  mutate: (data: Record<string, any>) => void
  isLoading: boolean
  isSuccess: boolean
  error: Error | null
}

const useCurrentUserMutation = (): CurrentUserMutationResult => {
  const [user, isLoading, authError] = useAuthState(auth)
  const queryClient = useQueryClient()
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    if (authError) {
      setError(authError)
    }
  }, [authError])

  const ref = doc(collection(db, "users"), user?.uid || "a")
  // Fix: separate the mutation options from the SetOptions for Firebase
  const mutation = useFirestoreDocumentMutation(
    ref,
    { merge: true }, // These are the Firebase SetOptions
    {
      // These are the mutation options
      onError: (err) => {
        console.error("Error in useCurrentUserMutation:", err)
        setError(err as Error)
      },
    },
  )

  useEffect(() => {
    if (!mutation.isLoading && mutation.isSuccess) {
      queryClient.invalidateQueries(["users", user?.uid])
      queryClient.removeQueries(["users", user?.uid])
    }
  }, [mutation.isLoading, mutation.isSuccess, queryClient, user?.uid])

  return {
    ...mutation,
    error: error || mutation.error,
    isLoading: isLoading || mutation.isLoading,
  }
}

interface CurrentUserMutationDeleteResult {
  mutate: (fields: string[]) => void
  isLoading: boolean
  isSuccess: boolean
  error: Error | null
}

const useCurrentUserMutationDelete = (): CurrentUserMutationDeleteResult => {
  const [user, isLoading, authError] = useAuthState(auth)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    if (authError) {
      setError(authError)
    }
  }, [authError])

  const mutationDelete = useMutation({
    mutationFn: async (fields: string[]) => {
      try {
        if (!user?.uid) {
          throw new Error("User not authenticated")
        }

        const docref = doc(db, "users", user.uid)

        const fields_delete: Record<string, any> = {}
        fields.forEach((i) => {
          Object.assign(fields_delete, {
            [i]: deleteField(),
          })
        })

        return await updateDoc(docref, fields_delete)
      } catch (err) {
        console.error("Error in useCurrentUserMutationDelete:", err)
        throw err
      }
    },
    onError: (err) => {
      console.error("Mutation error in useCurrentUserMutationDelete:", err)
      setError(err as Error)
    },
  })

  return {
    ...mutationDelete,
    error: error || (mutationDelete.error as Error),
    isLoading: isLoading || mutationDelete.isLoading,
  }
}

interface PensionApplicationStepsResult {
  currentStep: number
  totalSteps: number
  isLoading: boolean
  error: Error | null
}

const usePensionApplicationSteps = (): PensionApplicationStepsResult => {
  const { data: userData, isLoading, error: userError } = useGetUser()
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    if (userError) {
      setError(userError)
    }
  }, [userError])

  const obj = [
    {
      completed: userData?.pension_applied_activity_information ?? false,
    },
    {
      completed: userData?.pension_applied_verify_identity ?? false,
    },
    {
      completed: userData?.pension_applied_prsa_form ?? false,
    },
  ]

  if (userData?.employment_status === "Not currently working" || userData?.employment_status === "Employee") {
    obj.shift()
  }

  const steps = obj.filter((i) => i.completed).length

  return {
    currentStep: steps + 1,
    totalSteps: obj.length,
    isLoading: isLoading,
    error,
  }
}

export {
  useCurrentUserMutation,
  useCurrentUserMutationDelete,
  useGetUser,
  useGetUserBookings,
  usePensionApplicationSteps,
}
